import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BookOpen, Play, Pause, Coins, ShoppingBag, FileText, Package, Star, Send, ShieldCheck } from "lucide-react";
import { api, resolveAudio } from "@/lib/api";
import ShareButton from "@/components/ShareButton";

/**
 * Public reader page for a Fliboe Flip audiobook.
 * Route: /book/:slug. Streams chapters linearly + shows Buy buttons that
 * link to the author's own external sales page (Stripe Payment Link / Gumroad
 * / PayPal). Fliboe never touches the money.
 */
export default function BookReader() {
  const { slug } = useParams();
  const [book, setBook] = useState(null);
  const [err, setErr] = useState("");
  const [nowPlaying, setNowPlaying] = useState(null);
  const audioRef = useRef(null);

  // Reviews
  const [reviews, setReviews] = useState({ reviews: [], avg_stars: null, review_count: 0 });
  const [review, setReview] = useState({ reader_name: "", stars: 5, text: "" });
  const [submitting, setSubmitting] = useState(false);
  const [reviewMsg, setReviewMsg] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/books/by-slug/${slug}`);
        setBook(data);
        // SEO — Book schema + canonical + title
        document.title = `${data.title} — ${data.author_name || "Fliboe Flip"}`;
        const desc = document.querySelector('meta[name="description"]');
        if (desc && data.description) desc.setAttribute("content", data.description.slice(0, 155));
        const canonical = `${window.location.origin}/flip/b/${data.book_id}`;
        let can = document.querySelector('link[rel="canonical"]');
        if (!can) { can = document.createElement("link"); can.setAttribute("rel", "canonical"); document.head.appendChild(can); }
        can.setAttribute("href", canonical);
        const abs = (u) => (u && u.startsWith("/") ? window.location.origin + u : u);
        const jsonLd = {
          "@context": "https://schema.org",
          "@type": "Book",
          "name": data.title,
          "author": data.author_name ? { "@type": "Person", "name": data.author_name } : undefined,
          "description": data.description || `${data.title} on Fliboe Flip`,
          "image": abs(data.cover_url),
          "url": canonical,
          "bookFormat": "https://schema.org/AudiobookFormat",
          "inLanguage": data.language || "en",
          "genre": data.category,
          "publisher": { "@type": "Organization", "name": "Fliboe Flip" },
          "aggregateRating": data.rating_avg ? {
            "@type": "AggregateRating",
            "ratingValue": data.rating_avg,
            "reviewCount": data.rating_count || 1,
          } : undefined,
        };
        let s = document.getElementById("book-jsonld");
        if (!s) { s = document.createElement("script"); s.type = "application/ld+json"; s.id = "book-jsonld"; document.head.appendChild(s); }
        s.textContent = JSON.stringify(jsonLd);
        try { const r = await api.get(`/books/${data.book_id}/reviews`); setReviews(r.data); } catch (_) {}
      } catch (e) { setErr(e?.response?.data?.detail || "Book not found"); }
    })();
  }, [slug]);

  const play = (ch) => {
    if (!ch.audio_url) return;
    if (nowPlaying === ch.chapter_id) { audioRef.current?.pause(); setNowPlaying(null); return; }
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(resolveAudio(ch.audio_url));
    a.play().then(() => { audioRef.current = a; setNowPlaying(ch.chapter_id); }).catch(() => {});
    a.onended = () => setNowPlaying(null);
  };

  const openBuy = (product) => {
    // Prefer the author's external checkout link when they've set one (Stripe
    // Payment Link / Gumroad / PayPal). If not, fall back to a mailto so the
    // buyer can at least reach the author — this stops the buy buttons from
    // silently vanishing when the author sets prices but forgets the link.
    if (book?.sales_link) {
      const url = new URL(book.sales_link.startsWith("http") ? book.sales_link : `https://${book.sales_link}`);
      url.searchParams.set("product", product);
      url.searchParams.set("book", book.slug || "");
      window.open(url.toString(), "_blank", "noopener");
      return;
    }
    if (book?.author_email) {
      const subject = encodeURIComponent(`Buying ${book.title} — ${product}`);
      const body = encodeURIComponent(`Hi ${book.author_name || ""},\n\nI'd like to buy the ${product} of "${book.title}". How should I pay?\n\nThanks!`);
      window.location.href = `mailto:${book.author_email}?subject=${subject}&body=${body}`;
      return;
    }
    if (book?.author_slug) {
      window.open(`/author/${book.author_slug}`, "_blank", "noopener");
    }
  };

  const submitReview = async () => {
    setReviewMsg("");
    if (!review.reader_name.trim() || !review.stars) { setReviewMsg("Name and stars are required."); return; }
    setSubmitting(true);
    try {
      await api.post(`/books/${book.book_id}/reviews`, review);
      setReview({ reader_name: "", stars: 5, text: "" });
      const r = await api.get(`/books/${book.book_id}/reviews`);
      setReviews(r.data);
      setReviewMsg("Thanks — your review is live.");
    } catch (e) { setReviewMsg(e?.response?.data?.detail || "Could not submit review."); }
    finally { setSubmitting(false); }
  };

  if (err) return <main className="max-w-2xl mx-auto p-10 text-slate-400" data-testid="book-error">{err}</main>;
  if (!book) return <main className="max-w-2xl mx-auto p-10 text-slate-400">Loading…</main>;

  // Show buy buttons whenever a price is set. If the author has NOT entered a
  // sales_link, we fall back to a mailto in openBuy() so buyers can still
  // reach out — previously these buttons vanished, which is why "Flip only
  // shows description and audio".
  const freeDownload = book.free_pdf === true && !!book.pdf_url;
  const showBuyPdf    = !freeDownload && (book.pdf_price || 0) > 0    && book.for_sale !== false;
  const showBuyAudio  = !freeDownload && (book.audio_price || 0) > 0  && book.for_sale !== false;
  const showBuyBundle = !freeDownload && (book.bundle_price || 0) > 0 && book.for_sale !== false;
  const anyBuy = showBuyPdf || showBuyAudio || showBuyBundle;
  const noSalesLink = anyBuy && !book.sales_link;

  return (
    <main className="max-w-3xl mx-auto px-5 py-10" data-testid="book-reader">
      <div className="fl-card p-6 md:flex md:gap-6">
        {book.cover_url ? (
          <img src={book.cover_url} alt="" className="w-40 h-56 object-cover rounded-xl border border-white/10 shrink-0 mx-auto md:mx-0 mb-4 md:mb-0" />
        ) : (
          <div className="w-40 h-56 rounded-xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 border border-white/10 shrink-0 flex items-center justify-center mx-auto md:mx-0 mb-4 md:mb-0">
            <BookOpen className="w-10 h-10 text-white/40" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-violet-400 fl-mono text-[10px] tracking-[0.3em] uppercase">// Fliboe Flip</div>
          <h1 className="fl-display text-3xl font-black text-white mt-2">{book.title}</h1>
          <div className="text-white/60 text-sm mt-1">
            by{" "}
            {book.author_slug ? (
              <Link to={`/author/${book.author_slug}`} className="text-violet-300 hover:underline" data-testid="book-author-link">{book.author_name}</Link>
            ) : (
              <span>{book.author_name}</span>
            )}
            {" · "}{book.language} · {(book.chapters || []).length} chapters
          </div>
          {reviews.review_count > 0 && (
            <div className="mt-2 text-emerald-300 text-sm flex items-center gap-1" data-testid="book-avg-stars">
              <Star className="w-4 h-4 fill-emerald-400 text-emerald-400" /> {reviews.avg_stars} <span className="text-white/40 text-xs ml-1">· {reviews.review_count} review{reviews.review_count === 1 ? "" : "s"}</span>
            </div>
          )}
          {book.description && <p className="text-white/70 text-sm mt-3 leading-relaxed">{book.description}</p>}

          {freeDownload && (
            <div className="mt-5" data-testid="book-free-download">
              <a
                href={book.pdf_url}
                download
                target="_blank"
                rel="noreferrer"
                className="fl-btn !py-2 !px-3 text-sm !bg-emerald-500 !text-black hover:!bg-emerald-400"
                data-testid="book-download-free"
              >
                <FileText className="w-4 h-4" /> Download PDF · Free
              </a>
              <div className="mt-2 text-[11px] text-emerald-300 fl-mono">The author has made this book free to download.</div>
            </div>
          )}

          {anyBuy && (
            <div className="mt-5" data-testid="book-buy-buttons">
              <div className="flex flex-wrap gap-2">
                {showBuyPdf && (
                  <button onClick={() => openBuy("pdf")} className="fl-btn !py-2 !px-3 text-sm" data-testid="book-buy-pdf">
                    <FileText className="w-4 h-4" /> Buy PDF · ${book.pdf_price}
                  </button>
                )}
                {showBuyAudio && (
                  <button onClick={() => openBuy("audio")} className="fl-btn !py-2 !px-3 text-sm" data-testid="book-buy-audio">
                    <ShoppingBag className="w-4 h-4" /> Buy audiobook · ${book.audio_price}
                  </button>
                )}
                {showBuyBundle && (
                  <button onClick={() => openBuy("bundle")} className="fl-btn !py-2 !px-3 text-sm bg-violet-500 hover:bg-violet-400" data-testid="book-buy-bundle">
                    <Package className="w-4 h-4" /> Bundle PDF + Audio · ${book.bundle_price}
                  </button>
                )}
              </div>
              {noSalesLink && (
                <div className="mt-2 text-[11px] text-white/50 fl-mono" data-testid="book-buy-fallback">
                  Clicking buy will open an email to the author — they&apos;ll send you a payment link.
                </div>
              )}
            </div>
          )}

          {/* Share */}
          <div className="mt-4">
            <ShareButton
              app="flip"
              title={`${book.title} — by ${book.author_name}`}
              text={`Listen to "${book.title}" on Fliboe Flip · ${(book.chapters || []).length} chapter${(book.chapters || []).length === 1 ? "" : "s"} narrated by AI.`}
              url={`/books/${book.slug || book.book_id}`}
              badge={book.pdf_price ? `PDF $${book.pdf_price}` : "Audiobook"}
              cover={book.cover_url}
              showCopy
              testid="book-share"
            />
          </div>
        </div>
      </div>

      <div className="mt-8 space-y-2" data-testid="book-chapter-list">
        {(book.chapters || []).sort((a, b) => a.position - b.position).map(ch => {
          const isPlaying = nowPlaying === ch.chapter_id;
          return (
            <div key={ch.chapter_id} className="fl-card p-4" data-testid={`book-ch-${ch.chapter_id}`}>
              <div className="flex items-center gap-3">
                <div className="text-white/40 text-xs w-8 shrink-0">Ch {ch.position}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-white font-semibold truncate">{ch.title}</div>
                  <div className="text-white/40 text-xs">{Math.round(ch.duration || 0)}s{ch.is_free ? "" : " · Premium preview"}</div>
                </div>
                {ch.audio_url ? (
                  <button onClick={() => play(ch)} className="fl-btn ghost !py-2 !px-3" data-testid={`book-ch-play-${ch.chapter_id}`}>
                    {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                ) : (
                  <span className="text-white/30 text-xs" data-testid={`book-ch-no-audio-${ch.chapter_id}`}>No audio</span>
                )}
              </div>
              {isPlaying && ch.text && (
                <div className="mt-4 pt-4 border-t border-white/10" data-testid={`book-ch-text-${ch.chapter_id}`}>
                  <div className="fl-mono text-[10px] uppercase tracking-widest text-emerald-300 mb-2">// Read along</div>
                  <div className="text-white/85 leading-relaxed whitespace-pre-wrap max-h-[420px] overflow-auto pr-2 text-[15px]">
                    {ch.text}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {(book.chapters || []).length === 0 && (
          <div className="text-white/40 text-sm text-center py-8">No chapters published yet.</div>
        )}
      </div>

      {/* Tip jar */}
      {(book.tip_jar_url || book.author_tip_jar_url) && (() => {
        const raw = book.tip_jar_url || book.author_tip_jar_url;
        const href = raw.startsWith("http") ? raw : `https://${raw}`;
        return (
          <div className="mt-10 text-center" data-testid="book-tip-section">
            <div className="text-white/50 text-sm mb-2">Enjoying this book?</div>
            <a
              href={href}
              target="_blank"
              rel="noreferrer noopener"
              className="fl-btn"
              data-testid="book-tip-btn"
            >
              <Coins className="w-4 h-4" /> Tip {book.author_name}
            </a>
          </div>
        );
      })()}

      {/* Reviews (social proof) */}
      <section className="mt-14" data-testid="book-reviews">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="fl-display text-2xl font-bold text-white">Reader reviews</h2>
          {reviews.review_count > 0 && (
            <div className="text-emerald-300 text-sm flex items-center gap-1">
              <Star className="w-4 h-4 fill-emerald-400 text-emerald-400" /> {reviews.avg_stars} · {reviews.review_count}
            </div>
          )}
        </div>

        {reviews.reviews.length === 0 ? (
          <div className="fl-card p-6 text-center text-slate-500 text-sm">No reviews yet — be the first below.</div>
        ) : (
          <div className="space-y-3">
            {reviews.reviews.map(r => (
              <div key={r.review_id} className="fl-card p-4" data-testid={`review-${r.review_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-emerald-300 flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`w-3.5 h-3.5 ${i < r.stars ? "fill-emerald-400 text-emerald-400" : "text-white/20"}`} />
                    ))}
                  </div>
                  <div className="text-white font-semibold text-sm">{r.reader_name}</div>
                  {r.verified && (
                    <span className="text-[10px] fl-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 inline-flex items-center gap-1" title="Verified buyer — author confirmed this reviewer purchased the book" data-testid={`review-verified-${r.review_id}`}>
                      <ShieldCheck className="w-2.5 h-2.5" /> verified
                    </span>
                  )}
                  <div className="text-white/30 text-[11px] fl-mono">{(r.created_at || "").slice(0, 10)}</div>
                </div>
                {r.text && <p className="text-slate-300 text-sm mt-2 leading-relaxed whitespace-pre-wrap">{r.text}</p>}
              </div>
            ))}
          </div>
        )}

        {/* Review form */}
        <div className="fl-card p-5 mt-6" data-testid="review-form">
          <h3 className="text-white font-bold mb-3">Leave a review</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <input className="fl-input" placeholder="Your name" value={review.reader_name} onChange={e => setReview({ ...review, reader_name: e.target.value })} data-testid="review-name" />
            <div className="flex items-center gap-2">
              <span className="text-white/50 text-xs fl-mono uppercase tracking-widest">Stars</span>
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} type="button" onClick={() => setReview({ ...review, stars: n })} className="p-0.5" data-testid={`review-star-${n}`}>
                  <Star className={`w-6 h-6 ${n <= review.stars ? "fill-emerald-400 text-emerald-400" : "text-white/30"}`} />
                </button>
              ))}
            </div>
          </div>
          <textarea rows={3} className="fl-input mt-3" placeholder="What did you think? (optional)" value={review.text} onChange={e => setReview({ ...review, text: e.target.value.slice(0, 2000) })} data-testid="review-text" />
          {reviewMsg && <div className={`text-sm mt-2 ${reviewMsg.startsWith("Thanks") ? "text-emerald-300" : "text-red-300"}`} data-testid="review-msg">{reviewMsg}</div>}
          <button onClick={submitReview} disabled={submitting} className="fl-btn mt-3" data-testid="review-submit">
            <Send className="w-4 h-4" /> {submitting ? "Sending…" : "Post review"}
          </button>
        </div>
      </section>
    </main>
  );
}
